import { Button, Flex, Select, Text } from '@chakra-ui/react';
import React from 'react';

import { PAGE_SIZES } from '@/pages/externals/pos-menu-masters/service';

type Props = {
  page: number;
  totalPages: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
};

/**
 * 表示するページ番号。先頭・末尾と現在ページの前後1つを出し、
 * 飛びがあるところに省略記号を挟みます（NOS と同じ形）。
 */
const pageItems = (page: number, totalPages: number): (number | '…')[] => {
  const pages = new Set<number>([1, totalPages, page - 1, page, page + 1]);
  // Set の spread は tsconfig の target では使えないため Array.from を使います。
  const visible = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const out: (number | '…')[] = [];
  visible.forEach((p, i) => {
    if (i > 0 && p - visible[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

/**
 * SCR009 No.12/No.13 のページネーション。テーブル内スクロール（二重スクロール）を
 * 使わず、NOS と同じページ送りにします。
 */
export const MenuMasterPagination: React.FC<Props> = ({
  page,
  totalPages,
  pageSize,
  total,
  onPage,
  onPageSize,
}) => {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Flex
      style={{ gap: '14px', marginTop: '16px' }}
      align="center"
      wrap="wrap"
      data-testid="list-pagination"
      data-gap="14px"
    >
      <Flex
        align="center"
        style={{ gap: '14px' }}
        data-testid="list-pagination-left"
        data-gap="14px"
      >
        <Text fontSize="xs" color="gray.600" style={{ whiteSpace: 'nowrap' }}>
          表示件数
        </Text>
        <Select
          size="sm"
          maxW="100px"
          value={pageSize}
          style={{ borderRadius: '4px' }}
          data-testid="list-page-size"
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="表示件数"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}件
            </option>
          ))}
        </Select>
      </Flex>

      <Text fontSize="xs" color="gray.600" data-testid="list-page-range">
        {total === 0 ? '0 件' : `${from}–${to} / 全 ${total} 件`}
      </Text>

      <Flex
        style={{ gap: '4px' }}
        align="center"
        ml="auto"
        wrap="wrap"
        data-testid="list-pagination-right"
        data-gap="4px"
      >
        <Button
          size="sm"
          variant="ghost"
          style={{ fontWeight: 400 }}
          isDisabled={page <= 1}
          data-testid="list-page-prev"
          onClick={() => onPage(page - 1)}
          aria-label="前のページ"
        >
          ‹
        </Button>

        {pageItems(page, totalPages).map((item, i) =>
          item === '…' ? (
            // eslint-disable-next-line react/no-array-index-key
            <Text key={`gap-${i}`} fontSize="xs" color="gray.400" px={1}>
              …
            </Text>
          ) : (
            <Button
              key={item}
              size="sm"
              variant={item === page ? 'solid' : 'outline'}
              colorScheme={item === page ? 'darkBlue' : 'gray'}
              style={{ fontWeight: item === page ? 700 : 400 }}
              data-testid={`list-page-${item}`}
              onClick={() => onPage(item)}
            >
              {item}
            </Button>
          )
        )}

        <Button
          size="sm"
          variant="ghost"
          style={{ fontWeight: 400 }}
          isDisabled={page >= totalPages}
          data-testid="list-page-next"
          onClick={() => onPage(page + 1)}
          aria-label="次のページ"
        >
          ›
        </Button>
      </Flex>
    </Flex>
  );
};
